import * as React from "react";
import {
  CaretDownIcon,
  CaretUpDownIcon,
  CaretUpIcon,
  DotsThreeIcon,
} from "@phosphor-icons/react";
import type { ComponentRenderProps } from "@json-render/react";
import { useBoundProp, useStateStore } from "@json-render/react";
import { AlertContainer, AlertDescription, AlertTitle } from "@erp/ui/alert";
import { Badge } from "@erp/ui/badge";
import { Button } from "@erp/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@erp/ui/card";
import { Checkbox } from "@erp/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@erp/ui/dropdown-menu";
import { Input } from "@erp/ui/input";
import { Label } from "@erp/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui/select";
import { Separator } from "@erp/ui/separator";
import { Skeleton } from "@erp/ui/skeleton";
import { Switch } from "@erp/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@erp/ui/table";
import { Textarea } from "@erp/ui/textarea";
import { APP_COMPONENTS } from "../catalog";
import type { AppValueFormat } from "../catalog";
import {
  EMPTY_VALUE,
  formatAppValue,
  formatDelta,
  isNumericFormat,
  statusTone,
} from "../format";
import { resolveHrefTemplate, withSafeColumnHrefs } from "../link";
import { readProps } from "../props";
import {
  cellText,
  clearFilter,
  columnMenu,
  filterRows,
  filterValues,
  ariaSort,
  rowKey,
  sortDirection,
  sortRows,
  toggleFilterValue,
} from "../table";
import type { AppTableFilters, AppTableRow, AppTableSort } from "../table";
import { IconByName } from "./icons";
import { AppLink } from "./links";
import { AppTableActionsContext, useAppScopeProps } from "./scope";

/**
 * React implementations of the Apps catalog on `@erp/ui`.
 *
 * Each component reads its resolved props through `readProps`, so a prop that
 * drifted from its schema degrades that one prop instead of the element.
 * Spacing and type follow DESIGN.md: an 8px rhythm, 14px body, color only for
 * status, the olive `agent` token only for agent-written content.
 */

function cx(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(" ");
}

const GAP = {
  none: "gap-0",
  xs: "gap-1",
  sm: "gap-2",
  md: "gap-3",
  lg: "gap-4",
  xl: "gap-6",
} as const;

const PADDING = {
  none: "p-0",
  xs: "p-1",
  sm: "p-2",
  md: "p-3",
  lg: "p-4",
  xl: "p-6",
} as const;

const ALIGN = {
  start: "items-start",
  center: "items-center",
  end: "items-end",
  stretch: "items-stretch",
} as const;

const JUSTIFY = {
  start: "justify-start",
  center: "justify-center",
  end: "justify-end",
  between: "justify-between",
} as const;

const GRID_COLUMNS = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
} as const;

const HEADING = {
  1: "text-3xl font-bold leading-[1.15] tracking-[-0.01em]",
  2: "text-2xl font-bold leading-[1.3]",
  3: "text-xl font-semibold leading-[1.3]",
  4: "text-lg font-semibold leading-[1.3]",
} as const;

const TEXT_VARIANT = {
  body: "text-sm",
  label: "text-sm font-medium",
  muted: "text-sm font-light leading-[1.65] text-muted-foreground",
  eyebrow:
    "text-xs font-medium uppercase leading-none tracking-[0.08em] text-muted-foreground",
} as const;

const TONE_TEXT = {
  default: "",
  muted: "text-muted-foreground",
  success: "text-green-600 dark:text-green-500",
  warning: "text-amber-600 dark:text-amber-500",
  destructive: "text-destructive",
} as const;

/**
 * Status badges are tinted per DESIGN.md — tone-colored border and text on a
 * light fill of the same hue — rather than solid, so a column of them reads as
 * status, not as a row of buttons. `@erp/ui`'s destructive variant is already
 * tinted.
 */
const BADGE_TINT = {
  success:
    "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200",
  warning:
    "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200",
} as const;

type AppBadgeTone = NonNullable<React.ComponentProps<typeof Badge>["variant"]>;

function AppBadge({
  tone,
  icon,
  children,
}: {
  tone: AppBadgeTone;
  icon?: string;
  children: React.ReactNode;
}) {
  return (
    <Badge
      variant={tone}
      className={
        tone === "success" || tone === "warning" ? BADGE_TINT[tone] : undefined
      }
    >
      {icon && <IconByName name={icon} size="xs" />}
      {children}
    </Badge>
  );
}

const CELL_ALIGN = {
  left: "text-left",
  right: "text-right",
  center: "text-center",
} as const;

/** Prop schemas come from the catalog so the two cannot drift. */
const S = APP_COMPONENTS;

// ── Layout ──────────────────────────────────────────────────────────────────

export function StackComponent({ element, children }: ComponentRenderProps) {
  const p = readProps(S.Stack.props, element.props);
  return (
    <div
      className={cx(
        "flex min-w-0 flex-col",
        GAP[p.gap ?? "sm"],
        p.padding && PADDING[p.padding],
        p.align && ALIGN[p.align],
      )}
    >
      {children}
    </div>
  );
}

export function InlineComponent({ element, children }: ComponentRenderProps) {
  const p = readProps(S.Inline.props, element.props);
  return (
    <div
      className={cx(
        "flex min-w-0 flex-row",
        GAP[p.gap ?? "sm"],
        ALIGN[p.align ?? "center"],
        p.justify && JUSTIFY[p.justify],
        p.wrap !== false && "flex-wrap",
      )}
    >
      {children}
    </div>
  );
}

export function GridComponent({ element, children }: ComponentRenderProps) {
  const p = readProps(S.Grid.props, element.props);
  return (
    <div
      className={cx(
        "grid min-w-0",
        GRID_COLUMNS[p.columns ?? 2],
        GAP[p.gap ?? "sm"],
      )}
    >
      {children}
    </div>
  );
}

export function CardComponent({ element, children }: ComponentRenderProps) {
  const p = readProps(S.Card.props, element.props);
  return (
    <Card className="min-w-0">
      {(p.title || p.description) && (
        <CardHeader>
          {p.title && <CardTitle>{p.title}</CardTitle>}
          {p.description && <CardDescription>{p.description}</CardDescription>}
        </CardHeader>
      )}
      <CardContent className="flex flex-col gap-2">{children}</CardContent>
    </Card>
  );
}

export function SectionComponent({ element, children }: ComponentRenderProps) {
  const p = readProps(S.Section.props, element.props);
  return (
    <section className="flex min-w-0 flex-col gap-2">
      <div className="flex flex-col gap-0.5">
        {p.title && (
          <h3 className="text-lg font-semibold leading-[1.3]">{p.title}</h3>
        )}
        {p.description && (
          <p className="text-sm font-light text-muted-foreground">
            {p.description}
          </p>
        )}
      </div>
      {children}
    </section>
  );
}

/**
 * Tabs share the selected value with their Tab children through context: the
 * renderer mounts every child, and each Tab hides itself unless it is selected.
 */
const SelectedTabContext = React.createContext<string | undefined>(undefined);

export function TabsComponent({
  element,
  children,
  bindings,
}: ComponentRenderProps) {
  const p = readProps(S.Tabs.props, element.props);
  const tabs = p.tabs ?? [];
  const [bound, setBound] = useBoundProp<string>(p.value, bindings?.value);
  const [local, setLocal] = React.useState<string | undefined>(undefined);
  // A bound value lives in state; a plain `value` is only the initial tab, so
  // the user's own choice wins over it. A stale or unknown value would select
  // nothing and blank every panel.
  const candidate = bindings?.value ? bound : (local ?? bound);
  const selected = tabs.some((tab) => tab.value === candidate)
    ? candidate
    : tabs[0]?.value;
  function select(value: string) {
    if (bindings?.value) setBound(value);
    else setLocal(value);
  }
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div role="tablist" className="flex gap-4 border-b border-border">
        {tabs.map((tab) => (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={tab.value === selected}
            onClick={() => select(tab.value)}
            className={cx(
              "-mb-px border-b-2 px-0.5 pb-1.5 text-sm font-medium transition-colors duration-150",
              tab.value === selected
                ? "border-foreground text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <SelectedTabContext value={selected}>{children}</SelectedTabContext>
    </div>
  );
}

export function TabComponent({ element, children }: ComponentRenderProps) {
  const p = readProps(S.Tab.props, element.props);
  const selected = React.use(SelectedTabContext);
  if (selected !== p.value) return null;
  return (
    <div role="tabpanel" className="flex min-w-0 flex-col gap-2">
      {children}
    </div>
  );
}

export function SeparatorComponent() {
  return <Separator />;
}

// ── Content ─────────────────────────────────────────────────────────────────

export function HeadingComponent({ element }: ComponentRenderProps) {
  const p = readProps(S.Heading.props, element.props);
  const level = p.level ?? 3;
  const Tag = (["h1", "h2", "h3", "h4"] as const)[level - 1];
  return <Tag className={HEADING[level]}>{p.text ?? ""}</Tag>;
}

export function TextComponent({ element }: ComponentRenderProps) {
  const p = readProps(S.Text.props, element.props);
  return (
    <p
      className={cx(
        TEXT_VARIANT[p.variant ?? "body"],
        p.tone && TONE_TEXT[p.tone],
      )}
    >
      {p.text ?? ""}
    </p>
  );
}

function FormattedValue({
  value,
  format,
  currency,
}: {
  value: unknown;
  format: AppValueFormat | undefined;
  currency: string | undefined;
}) {
  const text = formatAppValue(value, format, { currency });
  if (format === "badge" && text !== EMPTY_VALUE) {
    return <AppBadge tone={statusTone(value)}>{text}</AppBadge>;
  }
  return (
    <span
      className={cx(
        isNumericFormat(format) && "tabular-nums",
        // Codes (SO-1053, SKUs) break mid-identifier when they wrap.
        format === "mono" && "font-mono whitespace-nowrap",
        text === EMPTY_VALUE && "text-muted-foreground",
      )}
    >
      {text}
    </span>
  );
}

export function FieldComponent({ element }: ComponentRenderProps) {
  const p = readProps(S.Field.props, element.props);
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-xs font-medium uppercase leading-none tracking-[0.08em] text-muted-foreground">
        {p.label}
      </span>
      <span className="truncate text-sm">
        <FormattedValue
          value={p.value}
          format={p.format}
          currency={p.currency}
        />
      </span>
    </div>
  );
}

export function MetricComponent({ element }: ComponentRenderProps) {
  const p = readProps(S.Metric.props, element.props);
  const delta = p.delta;
  const good =
    delta !== undefined &&
    delta !== 0 &&
    delta > 0 === (p.positiveIsGood ?? true);
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-xs font-medium uppercase leading-none tracking-[0.08em] text-muted-foreground">
        {p.label}
      </span>
      <span className="text-xl font-semibold leading-tight tabular-nums">
        {formatAppValue(p.value, p.format ?? "number", {
          currency: p.currency,
        })}
      </span>
      {delta !== undefined && (
        <span className="text-xs text-muted-foreground">
          <span
            className={cx(
              "font-medium tabular-nums",
              delta === 0
                ? ""
                : good
                  ? TONE_TEXT.success
                  : TONE_TEXT.destructive,
            )}
          >
            {formatDelta(delta)}
          </span>
          {p.deltaLabel && <> {p.deltaLabel}</>}
        </span>
      )}
    </div>
  );
}

export function BadgeComponent({ element }: ComponentRenderProps) {
  const p = readProps(S.Badge.props, element.props);
  return (
    <AppBadge tone={p.tone ?? "default"} icon={p.icon}>
      {p.text ?? ""}
    </AppBadge>
  );
}

export function IconComponent({ element }: ComponentRenderProps) {
  const p = readProps(S.Icon.props, element.props);
  if (!p.name) return null;
  return (
    <IconByName
      name={p.name}
      size={p.size ?? "sm"}
      className={cx("shrink-0", p.tone && TONE_TEXT[p.tone])}
    />
  );
}

export function AlertComponent({ element }: ComponentRenderProps) {
  const p = readProps(S.Alert.props, element.props);
  return (
    <AlertContainer variant={p.tone ?? "default"}>
      <AlertTitle>{p.title}</AlertTitle>
      {p.description && <AlertDescription>{p.description}</AlertDescription>}
    </AlertContainer>
  );
}

export function AgentNoteComponent({
  element,
  children,
}: ComponentRenderProps) {
  const p = readProps(S.AgentNote.props, element.props);
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-lg border border-agent-border bg-agent-subtle p-3 text-sm">
      {p.title && <span className="font-medium text-agent">{p.title}</span>}
      <p className="whitespace-pre-line">{p.text}</p>
      {children}
    </div>
  );
}

export function EmptyStateComponent({ element, emit }: ComponentRenderProps) {
  const p = readProps(S.EmptyState.props, element.props);
  return (
    <div className="flex flex-col items-center justify-center gap-1 px-3 py-8 text-center">
      <span className="text-sm font-medium">{p.title}</span>
      {p.description && (
        <span className="max-w-sm text-sm font-light text-muted-foreground">
          {p.description}
        </span>
      )}
      {p.actionLabel && (
        <Button
          variant="outline"
          className="mt-2"
          onClick={() => emit("press")}
        >
          {p.actionLabel}
        </Button>
      )}
    </div>
  );
}

export function ProgressComponent({ element }: ComponentRenderProps) {
  const p = readProps(S.Progress.props, element.props);
  const value = Math.min(100, Math.max(0, p.value ?? 0));
  return (
    <div className="flex min-w-0 flex-col gap-1">
      {p.label && (
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{p.label}</span>
          <span className="tabular-nums">{Math.round(value)}%</span>
        </div>
      )}
      <div
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={p.label}
        className="h-1.5 w-full overflow-hidden rounded-full bg-secondary"
      >
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-300"
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
}

const SKELETON_KEYS = Array.from({ length: 12 }, (_, i) => `line-${i + 1}`);

export function SkeletonComponent({ element }: ComponentRenderProps) {
  const p = readProps(S.Skeleton.props, element.props);
  const lines = p.lines ?? 3;
  return (
    <div className="flex flex-col gap-2" aria-busy="true">
      {SKELETON_KEYS.slice(0, lines).map((key, i) => (
        <Skeleton
          key={key}
          className={cx(
            "h-4",
            i === lines - 1 && lines > 1 ? "w-2/3" : "w-full",
          )}
        />
      ))}
    </div>
  );
}

// ── Data ────────────────────────────────────────────────────────────────────

type TableProps = ReturnType<typeof readProps<typeof S.Table.props>>;
type TableColumn = NonNullable<TableProps["columns"]>[number];
type RowAction = NonNullable<TableProps["rowActions"]>[number];

function columnAlign(column: TableColumn) {
  return CELL_ALIGN[
    column.align ?? (isNumericFormat(column.format) ? "right" : "left")
  ];
}

/** `rowActionLabel` is shorthand for a one-item `rowActions`. */
function rowActionsOf(p: TableProps): RowAction[] {
  const actions = [...(p.rowActions ?? [])];
  if (p.rowActionLabel) {
    actions.unshift({ value: "default", label: p.rowActionLabel });
  }
  return actions;
}

function SortIcon({
  direction,
}: {
  direction: AppTableSort["direction"] | false;
}) {
  if (direction === "asc") return <CaretUpIcon className="size-3" />;
  if (direction === "desc") return <CaretDownIcon className="size-3" />;
  return <CaretUpDownIcon className="size-3 opacity-50" />;
}

function SortMenuItems({
  columnKey,
  sorted,
  onSort,
}: {
  columnKey: string;
  sorted: boolean;
  onSort: (sort: AppTableSort | undefined) => void;
}) {
  return (
    <>
      <DropdownMenuItem
        onClick={() => onSort({ key: columnKey, direction: "asc" })}
      >
        <CaretUpIcon />
        Sort ascending
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={() => onSort({ key: columnKey, direction: "desc" })}
      >
        <CaretDownIcon />
        Sort descending
      </DropdownMenuItem>
      {sorted && (
        <DropdownMenuItem onClick={() => onSort(undefined)}>
          Clear sort
        </DropdownMenuItem>
      )}
    </>
  );
}

function FilterMenuItems({
  separated,
  values,
  kept,
  onToggleValue,
  onClear,
}: {
  /** Whether sort items sit above, so a separator divides the two. */
  separated: boolean;
  values: string[];
  kept: ReadonlySet<string> | undefined;
  onToggleValue: (value: string) => void;
  onClear: () => void;
}) {
  return (
    <>
      {separated && <DropdownMenuSeparator />}
      <DropdownMenuLabel>Show</DropdownMenuLabel>
      {values.map((value) => (
        <DropdownMenuCheckboxItem
          key={value}
          checked={kept?.has(value) ?? true}
          closeOnClick={false}
          onCheckedChange={() => onToggleValue(value)}
        >
          {value === EMPTY_VALUE ? "Blank" : value}
        </DropdownMenuCheckboxItem>
      ))}
      {kept && (
        <DropdownMenuItem onClick={onClear}>Clear filter</DropdownMenuItem>
      )}
    </>
  );
}

/**
 * A column header that sorts and filters through one menu, like the ERP's data
 * tables: sort either way, and keep or drop rows by the column's values.
 */
function TableColumnHeader({
  column,
  sort,
  onSort,
  values,
  kept,
  onToggleValue,
  onClearFilter,
}: {
  column: TableColumn;
  sort: AppTableSort | undefined;
  onSort: (sort: AppTableSort | undefined) => void;
  values: string[];
  kept: ReadonlySet<string> | undefined;
  onToggleValue: (value: string) => void;
  onClearFilter: () => void;
}) {
  // The menu is portaled out of the view; the scope keeps the App's colors.
  const scopeProps = useAppScopeProps();
  const { sortable, filterable } = columnMenu(column, values, Boolean(kept));
  if (!sortable && !filterable) return <>{column.label}</>;
  const direction = sortDirection(sort, column.key);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cx(
          "inline-flex items-center gap-1 rounded-sm uppercase tracking-[0.08em] hover:text-foreground data-[popup-open]:text-foreground",
          (direction || kept) && "text-foreground",
        )}
      >
        {column.label}
        <SortIcon direction={direction} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-40" {...scopeProps}>
        {sortable && (
          <SortMenuItems
            columnKey={column.key}
            sorted={Boolean(direction)}
            onSort={onSort}
          />
        )}
        {filterable && (
          <FilterMenuItems
            separated={sortable}
            values={values}
            kept={kept}
            onToggleValue={onToggleValue}
            onClear={onClearFilter}
          />
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function RowActionsMenu({
  rowLabel,
  actions,
  onSelect,
}: {
  /** Names the row for screen readers, so each row's menu button is distinct. */
  rowLabel: string;
  actions: RowAction[];
  onSelect: (action: RowAction) => void;
}) {
  const scopeProps = useAppScopeProps();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label={`Actions for ${rowLabel}`}
        >
          <DotsThreeIcon weight="bold" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" {...scopeProps}>
        {actions.map((action) => (
          <DropdownMenuItem
            key={action.value}
            variant={action.destructive ? "destructive" : "default"}
            onClick={() => onSelect(action)}
          >
            {action.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function TableComponent({ element, emit }: ComponentRenderProps) {
  const p = readProps(S.Table.props, withSafeColumnHrefs(element.props));
  const { set } = useStateStore();
  const renderActions = React.use(AppTableActionsContext);
  const columns = p.columns ?? [];
  const rows = p.rows ?? [];
  const actions = rowActionsOf(p);
  const hasActions = actions.length > 0;
  const [sort, setSort] = React.useState<AppTableSort | undefined>(undefined);
  const [filters, setFilters] = React.useState<AppTableFilters>(
    () => new Map(),
  );
  const shown = sortRows(filterRows(rows, columns, filters), sort, columns);
  function pressRow(row: AppTableRow, action: RowAction) {
    set("/selectedRow", row);
    set("/selectedRowAction", action.value);
    emit("rowPress");
  }
  return (
    <div className="min-w-0">
      {renderActions?.(
        columns.map((column) => column.label),
        shown.map((row) => columns.map((column) => cellText(row, column))),
      )}
      <div className="overflow-x-auto rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column) => {
                const values = filterValues(rows, column);
                return (
                  <TableHead
                    key={column.key}
                    aria-sort={ariaSort(sort, column.key)}
                    className={cx(
                      "text-[11px] font-medium uppercase tracking-[0.08em]",
                      columnAlign(column),
                    )}
                  >
                    <TableColumnHeader
                      column={column}
                      sort={sort}
                      onSort={setSort}
                      values={values}
                      kept={filters.get(column.key)}
                      onToggleValue={(value) =>
                        setFilters((current) =>
                          toggleFilterValue(current, column.key, value, values),
                        )
                      }
                      onClearFilter={() =>
                        setFilters((current) =>
                          clearFilter(current, column.key),
                        )
                      }
                    />
                  </TableHead>
                );
              })}
              {hasActions && <TableHead className="w-0" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (hasActions ? 1 : 0)}
                  className="py-6 text-center text-sm text-muted-foreground"
                >
                  {p.emptyText ?? EMPTY_VALUE}
                </TableCell>
              </TableRow>
            ) : (
              shown.map((row, index) => (
                <TableRow key={rowKey(row, index, columns)}>
                  {columns.map((column) => {
                    const href = column.href
                      ? resolveHrefTemplate(column.href, row)
                      : null;
                    const value = (
                      <FormattedValue
                        value={row[column.key]}
                        format={column.format}
                        currency={column.currency}
                      />
                    );
                    return (
                      <TableCell
                        key={column.key}
                        className={columnAlign(column)}
                      >
                        {href ? <AppLink href={href}>{value}</AppLink> : value}
                      </TableCell>
                    );
                  })}
                  {hasActions && (
                    <TableCell className="py-0 text-right">
                      <RowActionsMenu
                        rowLabel={
                          columns[0]
                            ? cellText(row, columns[0])
                            : String(index + 1)
                        }
                        actions={actions}
                        onSelect={(action) => pressRow(row, action)}
                      />
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export function ListComponent({ element }: ComponentRenderProps) {
  const p = readProps(S.List.props, element.props);
  const items = p.items ?? [];
  if (items.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-muted-foreground">
        {p.emptyText ?? EMPTY_VALUE}
      </p>
    );
  }
  return (
    <ul className="flex min-w-0 flex-col divide-y divide-border">
      {items.map((item) => (
        <li
          key={`${item.title}|${item.description ?? ""}|${item.meta ?? ""}`}
          className="flex items-start justify-between gap-3 py-2 first:pt-0 last:pb-0"
        >
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate text-sm font-medium">{item.title}</span>
            {item.description && (
              <span className="text-sm font-light text-muted-foreground">
                {item.description}
              </span>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {item.badge && (
              <AppBadge tone={item.badgeTone ?? statusTone(item.badge)}>
                {item.badge}
              </AppBadge>
            )}
            {item.meta && (
              <span className="text-xs text-muted-foreground tabular-nums">
                {item.meta}
              </span>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

// ── Input ───────────────────────────────────────────────────────────────────

export function ButtonComponent({ element, emit }: ComponentRenderProps) {
  const p = readProps(S.Button.props, element.props);
  return (
    <Button
      variant={p.variant ?? "outline"}
      size={p.size ?? "default"}
      disabled={p.disabled}
      onClick={() => emit("press")}
    >
      {p.icon && <IconByName name={p.icon} />}
      {p.label}
    </Button>
  );
}

function FieldShell({
  id,
  label,
  description,
  children,
}: {
  id: string;
  label: string | undefined;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {description && (
        <span className="text-xs text-muted-foreground">{description}</span>
      )}
    </div>
  );
}

export function TextInputComponent({
  element,
  bindings,
}: ComponentRenderProps) {
  const p = readProps(S.TextInput.props, element.props);
  const id = React.useId();
  const [value, setValue] = useBoundProp<string>(p.value, bindings?.value);
  return (
    <FieldShell id={id} label={p.label} description={p.description}>
      <Input
        id={id}
        type={p.type ?? "text"}
        value={value ?? ""}
        placeholder={p.placeholder}
        disabled={p.disabled}
        onChange={(event) => setValue(event.target.value)}
      />
    </FieldShell>
  );
}

export function TextAreaComponent({ element, bindings }: ComponentRenderProps) {
  const p = readProps(S.TextArea.props, element.props);
  const id = React.useId();
  const [value, setValue] = useBoundProp<string>(p.value, bindings?.value);
  return (
    <FieldShell id={id} label={p.label}>
      <Textarea
        id={id}
        rows={p.rows ?? 4}
        value={value ?? ""}
        placeholder={p.placeholder}
        disabled={p.disabled}
        onChange={(event) => setValue(event.target.value)}
      />
    </FieldShell>
  );
}

export function SelectComponent({ element, bindings }: ComponentRenderProps) {
  const p = readProps(S.Select.props, element.props);
  const id = React.useId();
  const [value, setValue] = useBoundProp<string>(p.value, bindings?.value);
  // The menu is portaled out of the view; the scope keeps the App's colors.
  const scopeProps = useAppScopeProps();
  return (
    <FieldShell id={id} label={p.label}>
      <Select
        value={value ?? ""}
        onValueChange={setValue}
        disabled={p.disabled}
      >
        <SelectTrigger id={id} className="w-full">
          <SelectValue placeholder={p.placeholder} />
        </SelectTrigger>
        <SelectContent {...scopeProps}>
          {(p.options ?? []).map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FieldShell>
  );
}

export function CheckboxComponent({ element, bindings }: ComponentRenderProps) {
  const p = readProps(S.Checkbox.props, element.props);
  const id = React.useId();
  const [checked, setChecked] = useBoundProp<boolean>(
    p.checked,
    bindings?.checked,
  );
  return (
    <div className="flex items-center gap-2">
      <Checkbox
        id={id}
        checked={checked ?? false}
        disabled={p.disabled}
        onCheckedChange={(next) => setChecked(next === true)}
      />
      <Label htmlFor={id}>{p.label}</Label>
    </div>
  );
}

export function SwitchComponent({ element, bindings }: ComponentRenderProps) {
  const p = readProps(S.Switch.props, element.props);
  const id = React.useId();
  const [checked, setChecked] = useBoundProp<boolean>(
    p.checked,
    bindings?.checked,
  );
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex flex-col gap-0.5">
        <Label htmlFor={id}>{p.label}</Label>
        {p.description && (
          <span className="text-xs text-muted-foreground">{p.description}</span>
        )}
      </div>
      <Switch
        id={id}
        checked={checked ?? false}
        disabled={p.disabled}
        onCheckedChange={setChecked}
      />
    </div>
  );
}
